"use client";

import React, { useState } from "react";
import Accordion from "./Accordion"; // Reuse the Accordion component for inner items.
import styles from "./AccordionFolder.module.css"; // Import AccordionFolder styles

/**
 * AccordionFolder Component
 *
 * Props:
 * - groups: Array of groups. Each group should be an object with:
 *     - groupHeader: A string or React node to display as the group's header.
 *     - items: An array of accordion items for that group; each item should have a `header` and `content` property.
 * - defaultOpenGroupIndex (optional): The index of the group to be open by default.
 * - groupCollapsible (optional): If false, prevents the groups from collapsing.
 * - innerAccordionProps (optional): Additional props to pass to each inner Accordion component.
 * - onGroupHeaderClick: (Optional) Callback fired when a group header is clicked, passes the group index.
 */
const AccordionFolder = ({
  groups,
  defaultOpenGroupIndex = null,
  groupCollapsible = true,
  innerAccordionProps = {},
  onGroupHeaderClick,
}) => {
  // Track multiple open group indices.
  const [openGroupIndices, setOpenGroupIndices] = useState(
    defaultOpenGroupIndex !== null ? [defaultOpenGroupIndex] : []
  );

  const toggleGroup = (index) => {
    setOpenGroupIndices((prevOpenGroupIndices) => {
      let newOpenGroupIndices;
      if (prevOpenGroupIndices.includes(index)) {
        newOpenGroupIndices = prevOpenGroupIndices.filter(i => i !== index);
      } else {
        newOpenGroupIndices = [...prevOpenGroupIndices, index];
      }
      if (onGroupHeaderClick) {
        onGroupHeaderClick(index);
      }
      return newOpenGroupIndices;
    });
  };

  return (
    <div className={styles.accordion}>
      {groups.map((group, groupIndex) => (
        <div key={groupIndex}>
          {/* Group Header */}
          <div
            className={`${styles.accordionHeader} ${openGroupIndices.includes(groupIndex) ? styles.active : ""}`}
            onClick={() => toggleGroup(groupIndex)}
          >
            {group.groupHeader}
          </div>
          {/* Group Content */}
          <div
            className={`${styles.accordionContent} ${openGroupIndices.includes(groupIndex) ? styles.open : ""}`}
            style={{
              height: openGroupIndices.includes(groupIndex) ? "auto" : "0",
            }}
          >
            <div className={styles.accordionInterior}>
              {/* Render inner Accordion for the group's items */}
              <Accordion
                items={group.items}
                collapsible={innerAccordionProps.collapsible !== undefined ? innerAccordionProps.collapsible : true}
                defaultOpenIndex={innerAccordionProps.defaultOpenIndex !== undefined ? innerAccordionProps.defaultOpenIndex : null}
                showCheckbox={innerAccordionProps.showCheckbox !== undefined ? innerAccordionProps.showCheckbox : false}
                onCheckboxChange={innerAccordionProps.onCheckboxChange} // Propagate checkbox changes.
                onHeaderClick={innerAccordionProps.onHeaderClick} // Propagate header clicks.
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AccordionFolder;
